import React from 'react';
import { StudioColumnSlot } from 'src/components/star-grid/components/StudioColumnSlot';
import Alert from '@amzn/awsui-components-core/alert';
import Button from '@amzn/awsui-components-core/button';
import SegmentedControl from '@amzn/awsui-components-core/segmented-control';
import type { NestedRowLayout } from 'src/components/star-grid/api/reportEngineTypes';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { useStudioDrop } from 'src/components/star-grid/hooks/useStudioDrop';
import { StudioFieldEditor } from 'src/components/star-grid/components/StudioFieldEditor';
import { StudioColumnEditor } from 'src/components/star-grid/components/StudioColumnEditor';
import { StudioParameterPill } from 'src/components/star-grid/components/StudioParameterPill';
import { StudioReorderSlot } from 'src/components/star-grid/components/StudioReorderSlot';
import { STUDIO_DRAG_TYPE, STUDIO_COLUMN_REORDER_TYPE, STUDIO_ROW_REORDER_TYPE } from 'src/components/star-grid/reportStudioConstants';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
import { StudioNestedRows } from 'src/components/star-grid/components/StudioNestedRows';
export const StudioShelves: React.FC<{ state: ReportStudioState }> = ({ state }) => {
  const drop = useStudioDrop(state);
  return (
    <>
      {drop.notice && (
        <Alert dismissible onDismiss={drop.dismiss}>
          {state.text(drop.notice as 'studioInvalidDrop')}
        </Alert>
      )}
      {state.rowLimitReached && (
        <Alert type="warning" dismissible onDismiss={state.dismissRowLimit} data-testid="studio-rows-limit">
          {state.rowLimitMessage}
        </Alert>
      )}
      {(['filters', 'rows'] as const).map((shelf) => (
        <section
          key={shelf}
          className={styles.shelf}
          data-testid={`studio-${shelf}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            drop.drop(event.dataTransfer.getData(STUDIO_DRAG_TYPE), shelf);
          }}
        >
          <header className={styles.shelfHeader}>
            <h2>{state.text(shelf === 'rows' ? 'studioRows' : 'studioFilters')}</h2>
            <span className={styles.spacer} />
            <small>{state.deck[shelf].length + (shelf === 'filters' ? state.template.parameters.length : 0)}</small>
          </header>
          <div className={styles.shelfBody}>
            <div
              className={shelf === 'filters' ? styles.filterList : styles.rowList}
              tabIndex={0}
              role="region"
              aria-label={state.text(shelf === 'filters' ? 'studioFilters' : 'studioRows')}
            >
              {shelf === 'filters' &&
                state.template.parameters.map((parameter) => <StudioParameterPill key={parameter.key} parameter={parameter} state={state} />)}
              {state.deck[shelf].map((field, index) => (
                <StudioReorderSlot
                  key={field.id}
                  index={index}
                  dragType={STUDIO_ROW_REORDER_TYPE}
                  label={field.binding_id}
                  disabled={shelf !== 'rows'}
                  onReorder={(from, to) => state.moveField(shelf, from, to - from)}
                >
                  <StudioFieldEditor
                    field={field}
                    shelf={shelf}
                    dataset={state.dataset}
                    lineSets={state.deck.line_sets}
                    text={state.text}
                    onChange={(next) => state.updateField(shelf, next)}
                    onRemove={() => state.removeField(shelf, field.id)}
                    onMove={(offset) => state.moveField(shelf, index, offset)}
                    onEditLineSet={shelf === 'rows' ? () => void state.openLineSet(field.id) : undefined}
                    picking={shelf === 'rows' && state.pickingRowId === field.id}
                    onPicked={(members, tree) => state.finishRowPick(field.id, members, tree)}
                    onCancelPick={() => state.cancelRowPick(field.id)}
                  />
                </StudioReorderSlot>
              ))}
            </div>
            {shelf === 'rows' && <StudioNestedRows axis={state.nestedRows} text={state.text} />}
            {shelf === 'rows' && state.hasSecondRowDimension && (
              <div className={styles.rowsLayout}>
                <span className={styles.rowsLayoutLabel}>{state.text('studioRowsLayoutLabel')}</span>
                <SegmentedControl
                  label={state.text('studioRowsLayoutLabel')}
                  data-testid="studio-rows-layout"
                  selectedId={state.nestedLayout}
                  onChange={({ detail }) => state.setNestedLayout(detail.selectedId as NestedRowLayout)}
                  options={[
                    { id: 'stack', text: state.text('studioRowsLayoutStack') },
                    { id: 'cross', text: state.text('studioRowsLayoutCross') }
                  ]}
                />
              </div>
            )}
            {!state.deck[shelf].length && !(shelf === 'filters' && state.template.parameters.length) && (
              <div className={styles.dropHint}>{state.text('studioDropDimensions')}</div>
            )}
          </div>
        </section>
      ))}
      <section
        className={styles.shelf}
        data-testid="studio-columns"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          drop.drop(event.dataTransfer.getData(STUDIO_DRAG_TYPE), 'columns');
        }}
      >
        <header className={styles.shelfHeader}>
          <h2>{state.text('studioColumns')}</h2>
          <span className={styles.spacer} />
          <small>{state.deck.columns.length}</small>
        </header>
        <div className={styles.shelfBody}>
          <div className={styles.columns} tabIndex={0} role="region" aria-label={state.text('studioColumns')}>
            {state.deck.columns.map((column, index) => (
              <StudioColumnSlot
                key={column.id}
                index={index}
                draggable
                label={column.label || state.text('studioColumnSettings')}
                onDrop={(event) => {
                  const from = event.dataTransfer.getData(STUDIO_COLUMN_REORDER_TYPE);
                  if (from !== '') {
                    // A column card was dragged onto this one -- reorder rather than add a field.
                    event.preventDefault();
                    event.stopPropagation();
                    const fromIndex = Number(from);
                    if (Number.isInteger(fromIndex) && fromIndex !== index) state.moveColumn(fromIndex, index - fromIndex);
                    return;
                  }
                  if (column.kind === 'scenario') {
                    event.preventDefault();
                    event.stopPropagation();
                    drop.drop(event.dataTransfer.getData(STUDIO_DRAG_TYPE), 'columns', column.id);
                  }
                }}
              >
                <StudioColumnEditor column={column} index={index} state={state} />
              </StudioColumnSlot>
            ))}
            <div className={styles.addBlock}>
              <span aria-hidden>+</span>
              <Button variant="inline-link" disabled={!state.draft.definition.active_deck} onClick={() => state.addScenario()}>
                {state.text('studioAddBlock')}
              </Button>
              <Button variant="inline-link" onClick={state.addComparison}>
                {state.text('studioAddVariance')}
              </Button>
              {state.dataset.bindings.some((binding) => binding.attributes.length > 0) && (
                <Button variant="inline-link" onClick={state.addAttribute}>
                  {state.text('studioAddAttribute')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
